import {
  MessagePayload,
  MessaggingSource,
  WorkerQueryResultMessagePayload,
} from "./types";
import { isErrorMessage } from "./utils";

import { WorkerQueryTransferModes } from "./consts";
import { decodeJSONBuffer } from "./encoding";
import { createChunksHandler } from "./send";

export function createErrorHandler(
  request: MessagePayload,
  resolve: (value: any) => void,
  reject: (reason?: any) => void
) {
  return (error: any) => {
    console.error("Service worker error event:", error);
    reject(error);
  };
}
export function createMessageErrorHandler(
  request: MessagePayload,
  resolve: (value: any) => void,
  reject: (reason?: any) => void
) {
  return (error: any) => {
    console.error("Service worker message error event:", error);
    reject(error);
  };
}
export function isResponseEvent<K extends string>(
  event: Event
): event is MessageEvent<WorkerQueryResultMessagePayload<K>> {
  const messageEvent = event as MessageEvent;
  return (
    messageEvent.data &&
    typeof messageEvent.data === "object" &&
    "type" in messageEvent.data &&
    "payload" in messageEvent.data
  );
}

export function createResponseHandler(
  request: MessagePayload,
  resolve: (value: any) => void,
  reject: (reason?: any) => void
) {
  const {
    opts: { verbose, request: requestOpts, response: responseOpts } = {},
    requestId,
  } = request;

  const chunksHandler = createChunksHandler(resolve, reject, request.opts);

  return <T>(event: Event) => {
    if (!isResponseEvent(event)) {
      if (verbose) {
        console.log("Received non-response event, ignoring:", event);
      }
      return;
    } else if (verbose) {
      console.log("Worker response message received:", event.data);
    }

    const {
      request: { id: responseId },
      payload,
      error,
    } = event.data;

    if (responseId !== requestId) {
      if (verbose) {
        console.log("Request ID does not match, ignoring message:", {
          expected: requestId,
          received: responseId,
        });
      }
    } else if (isErrorMessage(event.data)) {
      reject(error);
    } else if (
      responseOpts?.transferMode === WorkerQueryTransferModes.CHUNKED
    ) {
      chunksHandler.onMessage(event.data as any);
    } else {
      let response = responseOpts?.skipDecoding
        ? payload
        : decodeJSONBuffer<T>(payload, responseOpts);


      resolve(response);
    }
  };
}

export function createHandlers<T>(
  request: MessagePayload,
  resolve: (value: T | null) => void,
  reject: (reason?: any) => void
) {
  return {
    onmessage: createResponseHandler(request, resolve, reject),
    onerror: createErrorHandler(request, resolve, reject),
    onmessageerror: createMessageErrorHandler(request, resolve, reject),
  };
}
export async function listenForResponse<T>(
  channel: MessaggingSource,
  data: MessagePayload
) {
  const controller = new AbortController();
  return new Promise<T | null>((resolve, reject) => {
    const { onmessage, onerror, onmessageerror } = createHandlers<T>(
      data,
      (res) => resolve(res),
      reject
    );

    channel.addEventListener("message", onmessage, {
      signal: controller.signal,
    });

    channel.addEventListener("error", onerror, { signal: controller.signal });

    channel.addEventListener("messageerror", onmessageerror, {
      signal: controller.signal,
    });
  }).finally(() => {
    controller.abort();
  });
}
